<template>
  <div class="card mr-3 mb-4" style="height: 320px">
    <div class="px-4 pt-4 text-center">
      <div class="">
        <div class="d-flex justify-content-center">
          <h4 class="text-muted" style="font-size: 14px">Mentions By Daypart</h4>
          <i
            class="fas fa-info-circle text-muted ml-2 fa-sm"
            v-b-popover:filters.hover.bottom="disclaimer"
          ></i>
        </div>

        <div class="d-flex justify-content-end">
          <ul class="nav nav-tabs nav-tabs-sm">
            <li class="nav-item">
              <button
                type="button"
                class="btn-link nav-link py-1"
                :class="{ active: !selectedDay }"
                @click="setSelectedDay()"
              >
                Overall
              </button>
            </li>
            <li class="nav-item">
              <div class="btn-group drop-left" role="group">
                <button
                  id="btnGroupDrop1"
                  type="button"
                  class="btn-link dropdown-toggle nav-link py-1"
                  :class="{ active: selectedDay }"
                  data-toggle="dropdown"
                >
                  By Day
                </button>
                <div class="dropdown-menu" aria-labelledby="btnGroupDrop1">
                  <span
                    class="dropdown-item"
                    v-for="(value, key, index) in dayMap"
                    :key="index"
                    @click="setSelectedDay(value)"
                  >
                    {{ key }}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="py-3 px-4" style="height: 240px">
      <apexchart
        type="bar"
        height="100%"
        :series="selectMentionsByDaypart(selectedDay)"
        :options="chartOptions"
      />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import moment from 'moment'

const InsightsModule = createNamespacedHelpers('insightsv2')

export default {
  name: 'MentionsByDaypart',
  props: { selectedDay: { type: Number } },
  data: () => ({
    dayMap: { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 },
    disclaimer:
      'This chart only includes data for Ovation surveys because of inaccuracies relating to the timing of online reviews.'
  }),
  methods: {
    setSelectedDay(day) {
      this.$emit('selectedDay', day)
    },
    customToolTipLabels({ series, seriesIndex, dataPointIndex, w }) {
      const sentimentCount = series[seriesIndex][dataPointIndex]
      const daypart = w.globals.categoryLabels[dataPointIndex]
      const sentiment = seriesIndex === 0 ? 'negative' : 'positive'
      const sentimentColor = seriesIndex === 0 ? '#FC6D87' : '#33D691'

      // Get ALL entries for the specific daypart across all days
      const daypartEntries = this.$store.state.insightsv2.mentionsOverTime.filter(
        (d) => d.part && d.part.includes(daypart)
      )

      // Aggregate category breakdown from all entries for this daypart
      const aggregatedCategories = {}
      daypartEntries.forEach((entry) => {
        entry.categoryBreakdown.forEach((cat) => {
          if (!aggregatedCategories[cat.categoryId]) {
            aggregatedCategories[cat.categoryId] = {
              categoryName: cat.categoryName,
              positiveMentionCount: 0,
              negativeMentionCount: 0
            }
          }
          aggregatedCategories[cat.categoryId].positiveMentionCount += cat.positiveMentionCount
          aggregatedCategories[cat.categoryId].negativeMentionCount += cat.negativeMentionCount
        })
      })

      // Convert aggregated categories object to array and get category breakdown for the selected sentiment
      const categoryBreakdown = Object.values(aggregatedCategories)
        .filter((cat) =>
          sentiment === 'negative' ? cat.negativeMentionCount > 0 : cat.positiveMentionCount > 0
        )
        .sort((a, b) => {
          const aCount = sentiment === 'negative' ? a.negativeMentionCount : a.positiveMentionCount
          const bCount = sentiment === 'negative' ? b.negativeMentionCount : b.positiveMentionCount
          return bCount - aCount
        })
        .slice(0, 3)
        .map((cat) => ({
          name: cat.categoryName,
          count: sentiment === 'negative' ? cat.negativeMentionCount : cat.positiveMentionCount
        }))

      // Build template
      let template = `
      <div> 
        <small class="text-muted"> ${daypart} </small>
        <div class="d-flex mb-2">
         <div class="mt-2 mr-2" 
         style="height: 12px; width: 12px; border-radius: 6px; background-color: ${sentimentColor};">
         </div>
         <small class="mt-1 text-capitalize"> ${sentiment}: <strong> ${sentimentCount}%</strong></small>
        </div>
        <small class="text-muted"> Drivers </small>
        <div class="px-3 ml-1 mb-1">`

      // Append category breakdown to template
      categoryBreakdown.forEach((d) => {
        if (d.count > 0) {
          template += `<small class="mt-1"> ${d.name} - ${d.count}</small> <br>`
        }
      })

      // Append closing tags
      template += `</div></div>`
      return template
    }
  },
  computed: {
    ...InsightsModule.mapGetters(['selectMentionsByDaypart']),
    chartOptions() {
      return {
        chart: {
          height: 240,
          type: 'bar',
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        colors: ['#FC6D87', '#33D691'],
        plotOptions: {
          bar: {
            borderRadius: 12,
            columnWidth: '30%'
          }
        },
        grid: { show: true, borderColor: '#D2DDEC', strokeDashArray: 2 },
        noData: { text: 'No data to show' },
        dataLabels: { enabled: false },
        xaxis: {
          categories: ['Morning', 'Afternoon', 'Evening'],
          tooltip: { enabled: false },
          tickPlacement: 'on'
        },
        yaxis: {
          labels: { show: true },
          axisBorder: { show: true },
          max: 100,
          min: 0,
          tickAmount: 4,
          labels: { formatter: (val) => parseInt(val) }
        },
        tooltip: {
          enabled: true,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            return this.customToolTipLabels({ series, seriesIndex, dataPointIndex, w })
          }
        },
        stroke: { width: 0.5 },
        legend: { show: false }
      }
    }
  }
}
</script>
